import {
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import {
  RecaptchaModule,
  RECAPTCHA_LOADER_OPTIONS,
  RECAPTCHA_SETTINGS,
} from 'ng-recaptcha-2';
import { environment } from '../environments/environment';

import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { CustomTranslateLoader } from 'amatis-ui';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { NgxEchartsModule } from 'ngx-echarts';
import { routes } from './app.routes';
import { ErrorInterceptor } from './core/interceptors/error.interceptor';
import { MainLayoutService } from './pages/main/_main-layout/main-layout.service';
import { provideServiceWorker } from '@angular/service-worker';
import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeTr from '@angular/common/locales/tr';
import localeNl from '@angular/common/locales/nl';
import localeAr from '@angular/common/locales/ar';
import localeFr from '@angular/common/locales/fr';
import { LOCALE_ID } from '@angular/core';

registerLocaleData(localeEn, 'en-US');
registerLocaleData(localeTr, 'tr-TR');
registerLocaleData(localeNl, 'nl-NL');
registerLocaleData(localeAr, 'ar-AE');
registerLocaleData(localeFr, 'fr-FR');

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function localeFactory(): string {
  return localStorage.getItem('app-lang') || 'en-US';
}

export const appConfig: ApplicationConfig = {
  providers: [
    MainLayoutService,
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useFactory: localeFactory,
    },
    provideAngularSvgIcon(),
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: { siteKey: environment.recaptcha.siteKey },
    },
    {
      provide: RECAPTCHA_LOADER_OPTIONS,
      useFactory: (localeId: string) => {
        return {
          onBeforeLoad(url: URL) {
            const storedLang =
              localStorage.getItem('app-lang') || localeId || 'en-US';
            const normalizedLang = storedLang
              .split('-')[0]
              .toLowerCase();
            url.searchParams.set('hl', normalizedLang);
            return { url };
          },
        };
      },
      deps: [LOCALE_ID],
    },
    importProvidersFrom(
      FormsModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          useClass: CustomTranslateLoader,
          deps: [HttpClient],
        },
      }),
      NgxEchartsModule.forRoot({
        echarts: () => import('echarts'),
      }),
      NgSelectModule,
      FormsModule,
      BrowserAnimationsModule,
      RecaptchaModule
    ),
    provideServiceWorker('ngsw-worker.js', {
      enabled: true,
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
